// =====================================================================
//  TIPOS DEL DOMINIO
//
//  Escritos a mano contra el esquema de supabase/migrations. Cuando el
//  proyecto de Supabase esté enlazado, `npm run tipos` genera
//  src/tipos/supabase.ts desde la base de datos real y estos pasan a
//  derivarse de ahí; hasta entonces son la única fuente de tipos.
//
//  Nombres en español, igual que el esquema: mezclar idiomas entre la
//  base de datos y el código es una fuente de errores tontos.
//
//  Ojo con los `number` que vienen de columnas `numeric`: PostgREST las
//  entrega como string para no perder precisión. Las funciones de
//  src/dominio/formato.ts normalizan con num().
// =====================================================================

export type RolUsuario = 'MASTER' | 'ANALISTA' | 'LECTOR'

export type EstadoConcesion =
  | 'EN_REMATE'
  | 'RETIRADA'
  | 'ADJ_NOSOTROS'
  | 'ADJ_TERCERO'
  | 'TERRENO_FRANCO'
  | 'SUSPENDIDA'

export type NivelInteres = 'ALTA' | 'MEDIA' | 'BAJA' | 'DESCARTADA'

export type TipoRol = 'C' | 'T'

export type EstadoCausa =
  | 'TRAMITACION'
  | 'REMATE_FIJADO'
  | 'REMATADA'
  | 'SUSPENDIDA'
  | 'ARCHIVADA'

export type CodigoAlerta = 'DEUDA_CERO' | 'EN_TRAMITE' | 'SIN_VERIFICAR' | 'POSIBLE_LOTE'

/** Cifra que puede llegar como string desde una columna `numeric`. */
export type Cifra = number | string | null

export interface Usuario {
  id: number
  /** El máster permanente. No se puede degradar, desactivar ni eliminar. */
  protegido: boolean
  auth_id: string | null
  email: string
  nombre: string | null
  rol: RolUsuario
  activo: boolean
  ultimo_acceso: string | null
}

export interface Causa {
  id: number
  rol: string
  tribunal: string
  caratulado: string
  materia: string
  anio_proceso: number
  demandante: string | null
  abogado_patrocinante: string | null
  fecha_ingreso: string | null
  fecha_resolucion: string | null
  fecha_remate: string | null
  modalidad: 'PRESENCIAL' | 'VIDEOCONFERENCIA'
  enlace_remate: string | null
  fecha_publicacion: string | null
  estado: EstadoCausa
}

/** Fila de v_concesion — la vista maestra. */
export interface Concesion {
  id: number
  rol_nacional: string
  nombre: string
  ubicacion: string | null
  titular: string
  numero_orden: number
  comuna_id: number
  comuna: string
  provincia: string
  causa_id: number
  causa_rol: string
  tribunal: string
  fecha_remate: string | null
  fecha_publicacion: string | null
  folio_nomina: string
  nomina_numero: string
  valor_utm: Cifra
  monto_girado: Cifra
  monto_adeudado: Cifra
  utm_adeudadas: Cifra
  prop_desc: 'P' | 'D'
  fojas: string | null
  fojas_ref: string | null
  rol_numero: string | null
  rol_tipo: TipoRol
  rol_anio: number | null
  periodo: number
  /** = monto_adeudado. Art. 149: el mínimo es la deuda, no una tasación. */
  minimo_subasta: Cifra
  /** = monto_adeudado × 2. Art. 149 inc. 2. */
  costo_retiro_dueno: Cifra
  lote_id: number | null
  lote: string | null
  lote_confirmado: boolean
  estado: EstadoConcesion
  interes: NivelInteres
  postura_max: Cifra
  /** El mayor entre la postura y el mínimo legal. */
  compromiso: Cifra
  superficie_ha: Cifra
  fecha_pago_patente: string | null
  monto_pagado: Cifra
  comprobante_pago: string | null
  verificado_en: string | null
  monto_adjudicacion: Cifra
  adjudicatario: string | null
  notas: string | null
  actualizado: string | null
  actualizado_por: number | null
}

export interface Comuna {
  id: number
  nombre: string
  provincia: string
  region: string
  activa: boolean
  creado: string
}

/**
 * Hito del cronograma legal.
 *
 * El prototipo los traía escritos en el código. Están en tabla porque
 * varios van a cambiar: la fecha efectiva de publicación en el Diario
 * Oficial no se sabe todavía, y entre agosto y septiembre pueden aparecer
 * incidencias procesales.
 */
export interface Hito {
  id: number
  causa_id: number
  fecha: string
  titulo: string
  detalle: string | null
  articulo: string | null
  orden: number
}

export interface Criterio {
  codigo: string
  /** El valor con que se sembró, para «restaurar por defecto». */
  peso_defecto: number
  nombre: string
  descripcion: string | null
  automatico: boolean
  peso: number
  orden: number
  activo: boolean
}

/** Fila de v_puntaje_detalle. */
export interface PuntajeCriterio {
  concesion_id: number
  codigo: string
  nombre: string
  peso: number
  automatico: boolean
  orden: number
  evaluado: boolean
  valor: Cifra
}

/** Fila de v_puntaje. */
export interface Puntaje {
  concesion_id: number
  puntaje: number
  peso_total: number
  criterios_evaluados: number
  criterios_manuales: number
}

/** Fila de v_presupuesto. */
export interface Presupuesto {
  causa_id: number
  causa_rol: string
  presupuesto: Cifra
  presupuesto_desde: string | null
  comprometido: Cifra
  concesiones_marcadas: number
  saldo: Cifra
  /** Art. 148: la boleta cubre los lotes COMPLETOS que tocamos. */
  garantia_lotes: Cifra
  /** Lo que se obtendría sumando solo las marcadas. */
  garantia_solo_marcadas: Cifra
}

/** Fila de v_lote. */
export interface Lote {
  causa_id: number
  causa_rol: string
  lote: string
  lote_id: number | null
  comuna: string
  concesiones: number
  minimo_lote: Cifra
  garantia_exigida: Cifra
  aun_en_remate: number
  marcadas: number
  confirmado: boolean
}

/**
 * Fila de la tabla `lote` — el catálogo.
 *
 * Distinto de `Lote`, que es la fila agregada de v_lote. La distinción
 * importa: v_lote se construye desde `concesion`, así que un lote sin
 * concesiones asignadas no aparece ahí. Para poder asignarle la primera
 * hay que listar el catálogo.
 */
export interface LoteDefinido {
  id: number
  causa_id: number
  codigo: string
  descripcion: string | null
  confirmado: boolean
  creado: string
}

/** Fila de v_garantia_lote. */
export interface GarantiaLote {
  causa_id: number
  lote: string
  concesiones_lote: number
  garantia_lote: Cifra
  marcadas: number
  garantia_solo_marcadas: Cifra
}

export interface Alerta {
  concesion_id: number
  rol_nacional: string
  nombre: string
  alerta: CodigoAlerta
  detalle: string
}

export interface DueDiligence {
  concesion_id: number
  rol_nacional: string
  nombre: string
  total_items: number
  cumplidos: number
  avance_pct: number
}

export interface ChecklistItem {
  id: number
  texto: string
  orden: number
  activo: boolean
}

export interface ChecklistEstado {
  concesion_id: number
  item_id: number
  cumplido: boolean
  observacion: string | null
}

export interface Bitacora {
  id: number
  concesion_id: number | null
  usuario_id: number | null
  usuario_email: string | null
  accion: string
  campo: string | null
  valor_anterior: string | null
  valor_nuevo: string | null
  creado: string
}

/** Campos de `seguimiento` que el equipo edita desde la ficha. */
export interface CambioSeguimiento {
  estado?: EstadoConcesion
  interes?: NivelInteres
  postura_max?: number
  superficie_ha?: number | null
  fecha_pago_patente?: string | null
  monto_pagado?: number | null
  monto_adjudicacion?: number | null
  adjudicatario?: string | null
  verificado_en?: string | null
  notas?: string | null
}

// ---------------------------------------------------------------------
//  Etiquetas — las mismas del prototipo, incluidas las clases CSS
// ---------------------------------------------------------------------

export const ESTADOS: Record<EstadoConcesion, { t: string; c: string }> = {
  EN_REMATE: { t: 'En remate', c: 't-remate' },
  RETIRADA: { t: 'Retirada (pagó doble)', c: 't-pagada' },
  ADJ_NOSOTROS: { t: 'Adjudicada a nosotros', c: 't-nuestra' },
  ADJ_TERCERO: { t: 'Adjudicada a tercero', c: 't-tercero' },
  TERRENO_FRANCO: { t: 'Terreno franco', c: 't-franco' },
  SUSPENDIDA: { t: 'Suspendida', c: 't-tercero' },
}

export const INTERES: Record<NivelInteres, { t: string; c: string }> = {
  ALTA: { t: 'Alta', c: 't-alta' },
  MEDIA: { t: 'Media', c: 't-media' },
  BAJA: { t: 'Baja', c: 't-baja' },
  DESCARTADA: { t: 'Descartada', c: 't-desc' },
}

/**
 * Resumen de un artículo del Código de Minería.
 *
 * Está en la base y no en el código porque es texto de la ley: cambia
 * poco, pero cuando cambia lo hace por una reforma y no debería exigir un
 * despliegue. `codigo` y no `numero` porque «Sin postores» no lleva número.
 */
export interface Articulo {
  id: number
  codigo: string
  texto: string
  enlace: string | null
  orden: number
  activo: boolean
}

/** Qué puede hacer cada rol, para no repetir la matriz en la interfaz. */
export interface RolDescripcion {
  rol: RolUsuario
  etiqueta: string
  resumen: string
  orden: number
}
